import { DIMENSIONS, OBJECTIONS, OUTCOMES } from "./scorecard";
import { computeTotals, suggestLeakage } from "../utils/scoring";

// Demo data so the Statistics tab has something to show before there is any
// backend. Deterministic (seeded) so it looks the same on every reset.

const AGENTS = [
    { name: "Emma Bernard", skill: 0.8 },
    { name: "Michael Brooks", skill: 0.72 },
    { name: "Sofia Martin", skill: 0.66 },
    { name: "Noah Weber", skill: 0.6 },
    { name: "Chloe Dubois", skill: 0.55 },
    { name: "Daniel Romero", skill: 0.5 },
    { name: "Lars Lanau", skill: 0.42 },
    { name: "Abby Amber", skill: 0.36 },
];
const MANAGERS = ["Julien Moreau", "Sarah Klein", "Marc Lefevre"];
const TEAMS = ["Inbound", "Retention"];
const GEOS = ["DK", "NL", "EN", "DE", "FR", "ES"];

// Dimensions agents tend to struggle with, so leakage isn't uniformly random.
const DIMENSION_BIAS = { 4: -0.55, 8: -0.3, 11: -0.65, 12: -0.3, 13: -0.45, 14: -0.35, 7: -0.25 };

const COACHING_BY_LEAKAGE = {
    "Discovery": "Ask one more open question before moving to the scan.",
    "Value Creation": "Link every benefit to something the customer said earlier.",
    "Objection Handling": "Isolate the real objection before answering or discounting.",
    "Closing": "Ask for the decision, then stay silent.",
};

function createRandom(seed) {
    let state = seed;
    return () => {
        state = (state * 1664525 + 1013904223) % 4294967296;
        return state / 4294967296;
    };
}

const pick = (random, items) => items[Math.floor(random() * items.length)];

function toIsoDate(daysAgo) {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().slice(0, 10);
}

export function buildMockEvaluations(count = 48) {
    const random = createRandom(20261001);

    return Array.from({ length: count }, (_, index) => {
        const agent = pick(random, AGENTS);

        const dimensionScores = {};
        DIMENSIONS.forEach((dimension) => {
            const raw = agent.skill * 3 + (random() - 0.5) * 2.2 + (DIMENSION_BIAS[dimension.id] || 0);
            dimensionScores[dimension.id] = Math.max(0, Math.min(3, Math.round(raw)));
        });

        const { total } = computeTotals(dimensionScores);

        const roll = random();
        const saleChance = 0.1 + total * 0.65;
        let outcome;
        if (roll < saleChance) outcome = OUTCOMES[0];
        else if (roll < saleChance + 0.25) outcome = OUTCOMES[1];
        else if (roll < saleChance + 0.4) outcome = OUTCOMES[2];
        else if (roll < saleChance + 0.47) outcome = OUTCOMES[3];
        else outcome = OUTCOMES[4];

        const leakage = outcome === OUTCOMES[3] ? "Genuine Customer / Lead Limitation" : suggestLeakage(dimensionScores);
        const urgencyRoll = random();

        return {
            id: `seed-${String(index + 1).padStart(2, "0")}`,
            date: toIsoDate(Math.floor((index * 75) / count) + Math.floor(random() * 2)),
            team: pick(random, TEAMS),
            manager: pick(random, MANAGERS),
            agent: agent.name,
            callId: `CALL-${1000 + index * 7}`,
            geo: pick(random, GEOS),
            duration: `${6 + Math.floor(random() * 22)}:${String(Math.floor(random() * 60)).padStart(2, "0")}`,
            outcome,
            salesScore: total,
            dimensionScores,
            flagsRaised: Math.floor(random() * (total < 0.55 ? 6 : 3)),
            leakage,
            objection: random() < 0.25 ? OBJECTIONS[0] : pick(random, OBJECTIONS),
            urgency: urgencyRoll < 0.6 ? "Appropriate" : urgencyRoll < 0.85 ? "Too weak" : "Excessive",
            firstCloseMin: String(5 + Math.floor(random() * 18)),
            closeAttempts: String(Math.floor(random() * 4)),
            priorityShift: total >= 0.7 ? "Yes" : total >= 0.5 ? "Partially" : "No",
            bestMoment: "",
            coachingPriority: COACHING_BY_LEAKAGE[leakage] || "",
        };
    });
}

export const KNOWN_AGENTS = AGENTS.map((agent) => agent.name);
export const KNOWN_MANAGERS = MANAGERS;
export const KNOWN_TEAMS = TEAMS;
export const KNOWN_GEOS = GEOS;
